import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash-es';

const PAGE_SIZE = 10;

export function useUpdateSearchParams() {
    const [searchParams, setSearchParams] = useSearchParams();

    const updateSearchParams = useCallback(
        (key, value) => {
            // Updating search params.
            // Cloning the current search params.
            const newParams = new URLSearchParams(searchParams);

            if ((key === 'page' && value === 1) || (key !== 'page' && isEmpty(value))) {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
            // New Params.
            setSearchParams(newParams);
        },
        [searchParams, setSearchParams]
    );

    return { searchParams, updateSearchParams };
}

export function useOrderAndPaginationControl(pageSize = PAGE_SIZE) {
    const { searchParams, updateSearchParams } = useUpdateSearchParams();

    // Used to prevent infinite loop.
    const isInternalChange = useRef(false);

    const getInitialPage = useCallback(() => {
        const page = Number(searchParams.get('page'));
        return !Number.isNaN(page) && page > 0 ? page : 1;
    }, [searchParams]);

    const [orderBy, setOrderBy] = useState(() => searchParams.get('orderBy') || null);
    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [totalCount, setTotalCount] = useState(0);
    const currentPageRef = useRef(currentPage);

    // Keep ref in sync with currentPage.
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    useEffect(() => {
        updateSearchParams('orderBy', orderBy || '');
    }, [orderBy, updateSearchParams]);

    // Update the page param in the URL.
    useEffect(() => {
        if (!isInternalChange.current) {
            updateSearchParams('page', currentPage);
        }
        isInternalChange.current = false;
    }, [currentPage, updateSearchParams]);

    // Update the current page based on the page param in the URL.
    useEffect(() => {
        const page = getInitialPage();
        if (page !== currentPageRef.current) {
            isInternalChange.current = true;
            setCurrentPage(page);
        }
    }, [searchParams, getInitialPage]);

    return {
        orderBy,
        setOrderBy,
        pageSize,
        currentPage,
        setCurrentPage,
        totalCount,
        setTotalCount,
        pageCount: Math.ceil(totalCount / pageSize),
    };
}
